import type { TableHeadCellProps } from 'src/components/table';
import type { ITenant } from 'src/pages/tenants/tenants.types';
import type { ITicket } from 'src/pages/tickets/tickets.types';
import type { CSATSurvey } from 'src/pages/csat/slice/csatTypes';
import type { IAccount } from 'src/pages/accounts/accounts.types';
import type { AiAgent } from 'src/pages/automation/agents/slice/agentApi';
import type { ITeamItem, IUserItem } from 'src/pages/setting/setting.types';
import type { IContact, IDiscount } from 'src/pages/contacts/contacts.types';
import type { Workflow } from 'src/pages/automation/workflow/slice/workflowApi';
import type { ISnippet } from 'src/pages/setting/components/snippets/snippets.types';
import type { ILabel } from 'src/pages/setting/components/custom-labels/labels.types';
import type { IFieldItem } from 'src/pages/setting/components/custom-fields/slice/customFieldApi';
import type {
  KnowledgeGap,
  ConversationLearning,
} from 'src/pages/automation/agents/slice/agentLearningApi';
import type {
  TeamList,
  TicketsList,
  AccountList,
  ChannelData,
  TemplateList,
  BroadcastData,
  IConversation,
  CustomLabelsList,
} from 'src/types/user';

import { TableCustomView } from './table-custom-view';
import { useTableCustomLogic } from '../../hooks/use-table-custom-logic';

type TableCustomProps = {
  columns: TableHeadCellProps[];
  data:
    | TicketsList[]
    | IUserItem[]
    | IAccount[]
    | IContact[]
    | AccountList[]
    | TeamList[]
    | ITeamItem[]
    | CustomLabelsList[]
    | ChannelData[]
    | BroadcastData[]
    | BroadcastData[]
    | ILabel[]
    | TemplateList[]
    | ITenant[]
    | IConversation[]
    | IDiscount[]
    | ITicket[]
    | IFieldItem[]
    | AiAgent[]
    | Workflow[]
    | ISnippet[]
    | ConversationLearning[]
    | KnowledgeGap[]
    | CSATSurvey[]
    | any[];
  tableToolbar?: boolean;
  actionButton?: string;
  actionButtonHandler?: () => void;
  actionButtonIcon?: string;
  secondaryActionButton?: string;
  secondaryActionButtonHandler?: () => void;
  secondaryActionButtonIcon?: string;
  editHref: (id: string) => string;
  contactDetailHref?: (id: string) => string;
  accountDetailHref?: (id: string) => string;
  userDetailHref?: (id: string) => string;
  teamsDetailHref?: (id: string) => string;
  handleEditContact?: (id: string) => string | Promise<void>;
  tenantUserDetailsHref?: (tenantId: string, userId: string) => string;
  tenantInvoiceDetailsHref?: (tenantId: string, userId: string) => string;
  isToolbarVisible?: boolean;
  isCheckboxVisible?: boolean;
  rowHeight?: boolean;
  isSelectRowPremission?: boolean;
  selectRowPremission?: boolean;
  isActionButton?: Array<'import' | 'export'>;
  actionhref?: string;
  onQueryChange?: (newParams: any) => void;
  totalRecordLength?: number;
  sortFldList?: string[];
  isShowCreateBtn?: boolean;
  isSelectRecordDialog?: boolean;
  setRecordInputHandler?: (row: any) => void;
  isSearchFullWidth?: boolean;
  handleDeleteRecords?: (ids: string[]) => void;
  handleMergeRecords?: (ids: string[], clearSelection: () => void) => void;
  /** Replaces the built-in delete confirm for bulk delete (the page shows its own dialog) */
  handleBulkDeleteClick?: (ids: string[], clearSelection: () => void) => void;
  handleIVRConnection?: (id: string) => void;
  handleCustomiseDID?: (id: string) => void;
  handleToggleStatus?: (row: any) => void;
  handleQuickEdit?: (row: any) => void;
  moduleName?: string;
  loader?: boolean;
  handleResendMail?: (id: string) => string;
  handlePreview?: (id: any) => void;
  playCallRecording?: (id: string) => void;
  isCampaignTamplate?: boolean;
  getSelectedRows?: (id: any, selectAllFilter?: any) => void;
  isSelectAudience?: boolean;
  isCampaining?: boolean;
  onFileSelect?: (fileData: any) => void;
  onExportContact?: () => void;
  currentModule?: string;
  currentlyPlayingId?: string | null;
  advanceFilterFields?: any;
  isAdvanceFilter?: boolean;
  refreshPage?: () => void;
  refreshing?: boolean;
  tabFilters?: boolean;
  customTabs?: any;
  handleLoginAsUser?: (row: any) => void;
  handleOpenConversation?: (row: any) => void;
  isDisableSync?: boolean;
  userRole?: string;
  isSupportReport?: boolean;
  isSearchInput?: boolean;
  isPrivacyPolicyUrls?: {
    message: string;
    links: { label: string; url: string }[];
  } | null;
  displayPagination?: boolean;
  isTenantInvoice?: boolean;
  isSuperAdmin?: boolean;
  handleMarkAsRead?: (id: string) => void;
  currentTenantId?: string;
  handleAddFriend?: (row: any) => void;
  handlePayNowButton?: (id: any) => void;
  /** When set, invoice View / Pay Now are disabled and this text is shown as the reason. */
  invoiceLockedMessage?: string;
  handleCustomPermission?: (row: any) => Promise<boolean | void>;
  isTicketRedirectView?: boolean;
  handleApproveLearning?: (id: string) => void | Promise<void>;
  handleRejectLearning?: (id: string) => void | Promise<void>;
  isApprovingLearning?: boolean;
  isRejectingLearning?: boolean;
  handleResolveKnowledgeGap?: (id: string) => void | Promise<void>;
  isResolvingKnowledgeGap?: boolean;
  isRowInteractionLocked?: (row: any) => boolean;
  rowInteractionLockedTooltip?: string;
  getRowInteractionLockedTooltip?: (row: any) => string;
  customFields?: any[];
  disableSelectAll?: boolean;
  handleRefresh?: (id: string) => void;
  handleSmsQuickView?: (row: any) => void;
};

export function TableCustom(props: TableCustomProps) {
  // Use the logic hook to get all state, handlers, and grouped props
  const logic = useTableCustomLogic({
    columns: props.columns,
    data: props.data,
    moduleName: props.moduleName || '',
    rowHeight: props.rowHeight,
    tabFilters: props.tabFilters,
    onQueryChange: props.onQueryChange,
    isCampaignTamplate: props.isCampaignTamplate,
    isSelectAudience: props.isSelectAudience,
    isCampaining: props.isCampaining,
    getSelectedRows: props.getSelectedRows,
    totalRecordLength: props.totalRecordLength,
    sortFldList: props.sortFldList,
    selectRowPremission: props.selectRowPremission,
    isRowInteractionLocked: props.isRowInteractionLocked,
  });

  // Pass all props to the view component
  return (
    <TableCustomView
      {...props}
      {...logic}
      defaultBedgecondition={!!logic.defaultBedgecondition}
      handleCustomiseDID={props.handleCustomiseDID}
      handleIVRConnection={props.handleIVRConnection}
      handleMergeRecords={props.handleMergeRecords}
      actionButtonIcon={props.actionButtonIcon}
      secondaryActionButtonIcon={props.secondaryActionButtonIcon}
      disableSelectAll={props.disableSelectAll}
    />
  );
}
